<script setup lang="ts">
import { QDialog, QMenu } from 'quasar'
import { getCurrentInstance, onMounted, ref, type Ref } from 'vue'
import { Color } from '@previo/codebooks'
import { useHandleScrollQMenu } from '../../../../../composables/menu/handleScrollQMenu'
import { menuOffset } from '../../../composable/menuOffset'
import PBtnBadge from '../../../../btn-badge/PBtnBadge.vue'
import PCard from '../../../../card/PCard.vue'
import { PCardRound, PCardSize } from '../../../../card/types'
import PSpace from '../../../../space/PSpace.vue'
import { PSpaceSize } from '../../../../space/types'
import PTextTitle1 from '../../../../typography/title/PTextTitle1.vue'
import { usePrevioDS } from '../../../../../composables/helpers'
import { PMaxLines } from '../../../../typography/types'
import { type PPickerPosition, PPickerPositionEnum } from '../../PPickerPosition'
import type { UseHandleScroll } from '../../../../../composables/menu/types/UseHandleScroll'

type Props = {
  title: string
  cy: string
  anchor?: PPickerPosition
  self?: PPickerPosition
  offset?: number[]
  persistent?: boolean
  matchParentWidth?: boolean
  grow?: boolean
}

const isOpen = defineModel<boolean>()

withDefaults(defineProps<Props>(), {
  persistent: false,
  matchParentWidth: false,
  grow: false,
  anchor: PPickerPositionEnum.BOTTOM_END,
  self: PPickerPositionEnum.TOP_END,
  offset: () => menuOffset,
})

defineOptions({
  inheritAttrs: false,
})

const ds = usePrevioDS()

let instance: ReturnType<typeof getCurrentInstance> = null
const qMenuComponent = ref<InstanceType<typeof QMenu>>()
const handleScrollQMenu = ref<UseHandleScroll | null>(null)

const close = (): void => {
  isOpen.value = false
}

onMounted(() => {
  instance = getCurrentInstance()

  const parentEl = instance?.parent?.vnode.el

  if (!parentEl) {
    return
  }

  handleScrollQMenu.value = useHandleScrollQMenu(
    qMenuComponent as Ref<QMenu>,
    parentEl as HTMLElement,
  )
})
</script>

<template>
  <q-menu
    v-if="ds.screen().isLaptopAndLarge"
    ref="qMenuComponent"
    v-model="isOpen"
    v-bind="$attrs"
    :data-cy="cy"
    :persistent="persistent"
    :fit="matchParentWidth"
    :offset="offset"
    :anchor="anchor"
    :self="self"
    :class="{
      'ds-date-time-picker-context-menu--grow': grow,
    }"
    class="ds-date-time-picker-context-menu"
    no-parent-event
    no-refocus
    no-focus
    no-route-dismiss
    @show="handleScrollQMenu?.subscribe"
    @hide="handleScrollQMenu?.unsubscribe"
  >
    <p-card :pa-mobile="PCardSize.SM">
      <slot name="default" />
    </p-card>
  </q-menu>

  <q-dialog
    v-if="ds.screen().isMobile"
    v-model="isOpen"
    v-bind="$attrs"
    :title="title"
    :data-cy="cy"
    :persistent="persistent"
    :transition-duration="200"
    class="ds-picker-context-menu-dialog"
    transition-show="slide-up"
    transition-hide="slide-down"
    @close="close"
  >
    <p-card
      :color="Color.WHITE"
      class="ds-picker-context-menu-dialog__parent-card"
      :round-top-start="PCardRound.LG"
      :round-top-end="PCardRound.LG"
    >
      <p-card class="p-flex p-items-center" :pa-mobile="PCardSize.MD">
        <p-text-title1 :color="Color.PRIMARY" :text="title" :max-lines="PMaxLines.ONE" />
        <p-space :mobile="PSpaceSize.GROW" />
        <p-space :mobile="PSpaceSize.LG" />
        <p-btn-badge icon-start="xmark-large" cy="close" @click="close" />
      </p-card>

      <p-card :pa-mobile="PCardSize.MD" :pa-desktop="PCardSize.SM" :pa-laptop="PCardSize.SM">
        <slot name="default" />
      </p-card>
    </p-card>
  </q-dialog>
</template>

<style lang="scss">
.ds-date-time-picker-context-menu {
  &--grow {
    min-height: 332px !important;
  }

  max-height: unset !important;
}
</style>

<style lang="scss" scoped>
@import '../../../../../assets/scss/shared/mixins';

.ds-picker-context-menu-dialog {
  padding: 0;

  &__parent-card {
    @include safe-area-padding(bottom);
    width: 100% !important;
    bottom: 0 !important;
    position: absolute;
  }
}
</style>
